import type { ForMeCount } from '@nexus/core';
import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { api } from '../api/client';
import { notificationsQuery } from '../shell/notifications-panel';
import { useCanRemind } from '../reminders/api';
import { useCanTask } from '../tasks/api';
import { useCanApprove } from '../workflows/api';

/**
 * Number of things waiting for the person. No interval: it reloads when the window gets focus
 * again (default of TanStack Query), after own reminders and decisions, and when a new
 * notification arrives (docs/architecture/for-me.md).
 */
export const forMeCountQuery = queryOptions({
  queryKey: ['for-me', 'count'],
  queryFn: ({ signal }) => api.get<ForMeCount>('/for-me/count', { signal }),
});

/** Whether "Für mich" has anything to show: approvals, tasks or reminders in an active module. */
export function useShowForMe(): boolean {
  const canApprove = useCanApprove();
  const canRemind = useCanRemind();
  const canTask = useCanTask();
  return canApprove || canRemind || canTask;
}

/**
 * Entries of "Für mich" that need something now, for the badge; `undefined` while loading or
 * when the page is not shown.
 */
export function useForMeCount(): number | undefined {
  const show = useShowForMe();
  const client = useQueryClient();
  const { data } = useQuery({ ...forMeCountQuery, enabled: show });
  // Approvals, tasks and due reminders arrive as notifications: a new one is the moment to count again.
  // Reads the cache the bell keeps up to date, no request of its own.
  const latest = useQuery({
    ...notificationsQuery,
    enabled: false,
    select: (list) => list.items[0]?.id ?? null,
  }).data;
  const seen = useRef(latest);
  useEffect(() => {
    if (latest === seen.current) return;
    // The first answer of the bell is no news.
    const loaded = seen.current !== undefined;
    seen.current = latest;
    if (loaded) void client.invalidateQueries(forMeCountQuery);
  }, [client, latest]);
  return show && data ? data.approvals + data.reminders + data.tasks : undefined;
}
